import React from 'react';

type Language = 'en' | 'es';

interface NoticeSection {
  heading: string;
  paragraphs?: string[];
  bullets?: string[];
}

interface Translations {
  title: string;
  intro: string;
  contactLine: string;
  updatedLabel: string;
  updatedDate: string;
  sections: NoticeSection[];
  removalSubjectLabel: string;
  removalSubjectValue: string;
  removalBodyLabel: string;
  removalBodyValue: string;
  backButton: string;
}

interface TranslationsMap {
  en: Translations;
  es: Translations;
}

const translations: TranslationsMap = {
  en: {
    title: 'Privacy Notice',
    intro:
      'This notice explains how Andor Cloud handles information in this survey. It is written to support participants across different jurisdictions in clear language.',
    contactLine: 'Controller: Andor Cloud | Contact: andor.cloud@gmail.com',
    updatedLabel: 'Last updated',
    updatedDate: '2026-03-05',
    sections: [
      {
        heading: 'Purpose and Participation Terms',
        paragraphs: [
          'This survey is designed to understand environment-management practices and challenges in Drupal and frontend workflows. Participation is voluntary and intended to be anonymous.',
        ],
        bullets: [
          'You may skip any question and stop at any time.',
          'Please do not include confidential information or third-party personal data in free-text responses.',
          'Providing an email for follow-up is optional and separate from survey answers.',
        ],
      },
      {
        heading: 'Data We Collect',
        bullets: [
          'Survey responses about professional and technical practices.',
          'Country selection for regional aggregation.',
          'Optional follow-up email submitted after completion.',
        ],
      },
      {
        heading: 'Data Separation and Use',
        paragraphs: [
          'Survey responses are processed for research and reporting in aggregated form. Optional emails are used only to send survey follow-up updates when requested.',
          'The optional email list is handled independently and is not intentionally linked to survey responses.',
        ],
      },
      {
        heading: 'What We Do Not Do',
        bullets: [
          'No analytics or advertising trackers are used in this survey experience.',
          'No sale, rental, or brokerage of participant data.',
          'No unrelated marketing use of optional emails.',
        ],
      },
      {
        heading: 'Cookies and Local Storage',
        paragraphs: [
          'We use only technical storage needed for secure operation and a stable survey experience.',
        ],
        bullets: [
          'Cookie `sid`: server-managed session cookie for survey session continuity.',
          'Cookie `csrf`: security token cookie used to protect requests.',
          'Local storage `survey_state`: stores in-progress answers/page to avoid restarting after refresh.',
          'Local storage `survey_email_opt_in_sent`: stores whether optional follow-up email submission already succeeded.',
        ],
      },
      {
        heading: 'Legal Basis (Global Context)',
        paragraphs: [
          'Where required, survey processing relies on your voluntary participation/consent. Optional follow-up email processing relies on explicit opt-in. In some jurisdictions, aggregated research reporting may also rely on legitimate interest.',
        ],
      },
      {
        heading: 'Retention',
        bullets: [
          'Survey dataset retention: up to 6 months.',
          'Optional follow-up email retention: up to 6 months, or sooner upon request.',
        ],
      },
      {
        heading: 'Sharing and International Access',
        paragraphs: [
          'We do not share participant data for commercial resale. Limited infrastructure providers (for hosting, security, and email delivery) may process data strictly to operate this survey. Access may occur across jurisdictions with reasonable safeguards.',
        ],
      },
      {
        heading: 'Your Choices and Rights',
        bullets: [
          'You can participate without submitting an email.',
          'You can request removal of your optional follow-up email at any time.',
          'If your local law grants additional rights (such as access, correction, deletion, restriction, or objection), you can request support by email.',
        ],
      },
      {
        heading: 'How to Request Email Removal',
        paragraphs: [
          'Send your request to andor.cloud@gmail.com using the template below.',
        ],
      },
    ],
    removalSubjectLabel: 'Template subject',
    removalSubjectValue: 'Data Deletion Request - Andor Cloud Survey Email Opt-In',
    removalBodyLabel: 'Template body',
    removalBodyValue:
      'Hello Andor Cloud,\nI want to remove my email from the survey follow-up list.\nEmail to remove: [your-email@example.com]\nLanguage preference: [EN/ES]\nDate of request: [YYYY-MM-DD]\nThank you.',
    backButton: 'Back to Survey',
  },
  es: {
    title: 'Aviso de Privacidad',
    intro:
      'Este aviso explica como Andor Cloud trata la informacion de esta encuesta. El texto esta redactado para participantes de distintas jurisdicciones con lenguaje claro.',
    contactLine: 'Responsable: Andor Cloud | Contacto: andor.cloud@gmail.com',
    updatedLabel: 'Ultima actualizacion',
    updatedDate: '2026-03-05',
    sections: [
      {
        heading: 'Finalidad y Condiciones de Participacion',
        paragraphs: [
          'Esta encuesta busca comprender practicas y desafios de gestion de environments en flujos de trabajo Drupal y frontend. La participacion es voluntaria y esta pensada para ser anonima.',
        ],
        bullets: [
          'Puede omitir cualquier pregunta y retirarse en cualquier momento.',
          'No incluya informacion confidencial ni datos personales de terceros en respuestas de texto libre.',
          'Compartir un correo para seguimiento es opcional y separado de las respuestas de la encuesta.',
        ],
      },
      {
        heading: 'Datos que Recopilamos',
        bullets: [
          'Respuestas de la encuesta sobre practicas profesionales y tecnicas.',
          'Seleccion de pais para agregacion regional.',
          'Correo opcional de seguimiento enviado al finalizar.',
        ],
      },
      {
        heading: 'Separacion y Uso de Datos',
        paragraphs: [
          'Las respuestas de la encuesta se procesan con fines de investigacion y reporte agregado. Los correos opcionales se usan solo para enviar seguimiento de resultados cuando usted lo solicita.',
          'La lista de correos opcionales se maneja de forma independiente y no se vincula intencionalmente con las respuestas de la encuesta.',
        ],
      },
      {
        heading: 'Lo que No Hacemos',
        bullets: [
          'No usamos rastreadores de analitica ni publicidad en esta experiencia.',
          'No vendemos, alquilamos ni comercializamos datos de participantes.',
          'No usamos correos opcionales para marketing no relacionado.',
        ],
      },
      {
        heading: 'Cookies y Almacenamiento Local',
        paragraphs: [
          'Usamos solo almacenamiento tecnico necesario para operar con seguridad y mantener una experiencia estable.',
        ],
        bullets: [
          'Cookie `sid`: cookie de sesion gestionada por el servidor para continuidad de sesion.',
          'Cookie `csrf`: cookie de token de seguridad para proteger solicitudes.',
          'Local storage `survey_state`: guarda respuestas/pagina en progreso para evitar reinicio tras recargar.',
          'Local storage `survey_email_opt_in_sent`: guarda si el envio del correo opcional ya fue exitoso.',
        ],
      },
      {
        heading: 'Base Legal (Contexto Global)',
        paragraphs: [
          'Cuando aplique, el tratamiento de la encuesta se basa en su participacion voluntaria/consentimiento. El correo opcional se basa en su consentimiento explicito (opt-in). En algunas jurisdicciones, el reporte agregado de investigacion tambien puede basarse en interes legitimo.',
        ],
      },
      {
        heading: 'Conservacion',
        bullets: [
          'Conservacion del dataset de encuesta: hasta 6 meses.',
          'Conservacion del correo opcional de seguimiento: hasta 6 meses, o antes si usted lo solicita.',
        ],
      },
      {
        heading: 'Comparticion y Acceso Internacional',
        paragraphs: [
          'No compartimos datos para reventa comercial. Proveedores de infraestructura limitados (hosting, seguridad y envio de correo) pueden procesar datos estrictamente para operar esta encuesta. El acceso puede ocurrir entre jurisdicciones con salvaguardas razonables.',
        ],
      },
      {
        heading: 'Sus Opciones y Derechos',
        bullets: [
          'Puede participar sin enviar correo.',
          'Puede solicitar la eliminacion de su correo opcional en cualquier momento.',
          'Si su normativa local reconoce derechos adicionales (acceso, rectificacion, eliminacion, limitacion u oposicion), puede solicitar asistencia por correo.',
        ],
      },
      {
        heading: 'Como Solicitar Eliminacion del Correo',
        paragraphs: [
          'Envie su solicitud a andor.cloud@gmail.com usando la plantilla siguiente.',
        ],
      },
    ],
    removalSubjectLabel: 'Asunto sugerido',
    removalSubjectValue: 'Solicitud de Eliminacion de Datos - Correo de Seguimiento Andor Cloud Survey',
    removalBodyLabel: 'Cuerpo sugerido',
    removalBodyValue:
      'Hola Andor Cloud,\nQuiero eliminar mi correo de la lista de seguimiento de la encuesta.\nCorreo a eliminar: [tu-correo@ejemplo.com]\nIdioma de preferencia: [EN/ES]\nFecha de solicitud: [AAAA-MM-DD]\nGracias.',
    backButton: 'Volver a la Encuesta',
  },
};

interface LegalsPageProps {
  language: Language;
  onBack: () => void;
}

function LegalsPage({ language, onBack }: LegalsPageProps): React.ReactElement {
  const t = translations[language];

  return (
    <div className="legals-page">
      <div className="legals-page__content">
        <h1 className="legals-page__title">{t.title}</h1>
        <p className="legals-page__intro">{t.intro}</p>
        <p className="legals-page__meta">{t.contactLine}</p>
        <p className="legals-page__meta">
          {t.updatedLabel}: {t.updatedDate}
        </p>

        <div className="legals-page__sections">
          {t.sections.map((section) => (
            <section className="legals-page__section" key={section.heading}>
              <h2 className="legals-page__section-title">{section.heading}</h2>
              {section.paragraphs?.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
              {section.bullets && (
                <ul className="legals-page__list">
                  {section.bullets.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>

        <section className="legals-page__section">
          <h2 className="legals-page__section-title">{language === 'en' ? 'Removal Request Template' : 'Plantilla de Solicitud de Eliminacion'}</h2>
          <p className="legals-page__template-label">{t.removalSubjectLabel}</p>
          <pre className="legals-page__template">{t.removalSubjectValue}</pre>
          <p className="legals-page__template-label">{t.removalBodyLabel}</p>
          <pre className="legals-page__template">{t.removalBodyValue}</pre>
        </section>

        <button className="legals-page__button legals-page__button--floating" onClick={onBack}>
          {t.backButton}
        </button>
      </div>
    </div>
  );
}

export default LegalsPage;
